import React from 'react';
import { FC, useCallback, useState } from 'react';

import { Copy } from '@gravity-ui/icons';
import { Button, Icon, Label } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { shareApi } from 'client/api';
import { useShareLinkCopy } from 'client/hooks/useCopy';
import { useFetchingHandler } from 'client/hooks/useFetchingHandler';
import { InstallableApp, Skill } from 'client/model/skill';
import * as keyset from 'i18n/Access';

import { Field } from '../form/Field/Field';

import styles from './SharingPage.module.scss';

const t = i18n(keyset);

interface Props {
    aliceApp: InstallableApp | Skill;
    sharingAvailable?: boolean;
}

export const SharingPageGenerateLink: FC<Props> = ({ aliceApp, sharingAvailable }) => {
    const [oneTimeLink, setOneTimeLink] = useState('');
    const { buttonRef, onButtonClick, onCodeClick, textRef } = useShareLinkCopy();

    const [createOneTimeShare, generationState] = useFetchingHandler({
        handler: shareApi.createOneTimeShare,
        throwErrors: true,
    });

    const generateOneTimeLink = useCallback(async () => {
        try {
            const oneTimeShareKey = await createOneTimeShare({ skillId: aliceApp.id }, aliceApp.channel);

            const shareLink = `${__appConfig.app.shareBaseUrl}?key=${oneTimeShareKey}`;

            setOneTimeLink(shareLink);
        } catch (e) {
            console.error(e);
        }
    }, [aliceApp.id, aliceApp.channel, createOneTimeShare]);

    const isGenerating = generationState === 'loading';
    const isError = generationState === 'error';

    return (
        <Field
            fieldTitle={t('links-header')}
            fieldDescription={t('app-link-description')}
            error={isError ? t('error') : undefined}
        >
            <div
                className={clx(
                    'g-text-input',
                    'g-text-input_view_normal',
                    'g-text-input_size_xl',
                    'g-text-input_pin_round-round',
                    styles.link__container,
                )}
                onClick={isGenerating ? undefined : onCodeClick}
            >
                <div className={clx('g-text-input__content', styles.link__content)}>
                    <div
                        className={clx(styles.link__text, 'g-text-input__control', 'g-text-input__control_type_input')}
                        ref={textRef}
                    >
                        {oneTimeLink}
                    </div>

                    <Button
                        onClick={onButtonClick}
                        ref={buttonRef}
                        disabled={!oneTimeLink || isGenerating}
                        view="flat"
                        size="l"
                    >
                        <Icon data={Copy} />
                    </Button>
                </div>
            </div>
            <Button onClick={generateOneTimeLink} loading={isGenerating} size="l" disabled={!sharingAvailable}>
                {t('links-button-generate')}
            </Button>
            {sharingAvailable === false ? (
                <Label className={styles.sharingWarning} theme="warning" size="s">
                    {t('max-number-reached')}
                </Label>
            ) : null}
        </Field>
    );
};
